export const APP_VERSION = '0.2.0' as const;

export type UserRole = 'administrator' | 'engineer' | 'viewer';
export type SiteType = 'site' | 'base_station' | 'client';
export type LinkType = 'ptp' | 'ptmp';
export type LinkQuality = 'excellent' | 'good' | 'marginal' | 'blocked';
export type SurfaceSourceType = 'DSM' | 'DTM' | 'LIDAR' | 'MANUAL';

export interface Coordinates {
  lat: number;
  lon: number;
}

export interface ProjectViewState {
  center: Coordinates;
  zoom: number;
  pitch: number;
  bearing: number;
  activeLayers: string[];
  selectedObjectId: string | null;
}

export interface DesignSite {
  id: string;
  name: string;
  type: SiteType;
  coordinates: Coordinates;
  groundElevationM: number | null;
  antennaHeightM: number;
  equipmentName: string;
  frequencyGHz: number;
  channelWidthMHz: number;
  txPowerDbm: number;
  antennaGainDbi: number;
  cableLossDb: number;
  noiseFigureDb: number;
  ambientNoiseFloorDbm: number;
  /** Immutable catalog snapshot used to keep saved designs reproducible. */
  radioDeviceId?: string;
  radioMaxTxPowerDbm?: number | null;
  radioChannelWidthsMHz?: number[];
  radioReceiverSensitivityDbm?: number | null;
  radioMaxThroughputMbps?: number | null;
  radioMaxRangeKm?: number | null;
  radioPolarization?: EquipmentPolarization;
  radioPerformanceProfiles?: EquipmentPerformanceProfile[];
  antennaModelId?: string | null;
  antennaName?: string | null;
}

export type ObstacleKind = 'building' | 'vegetation' | 'tower' | 'tree' | 'manual';

export interface ManualObstacle {
  id: string;
  name: string;
  kind: ObstacleKind;
  coordinates: Coordinates;
  heightM: number;
}

export interface ApiHealth {
  status: 'ok' | 'degraded';
  version: string;
  database: 'connected' | 'unavailable';
  postgis: string | null;
  elevation: {
    primary: { provider: string; status: 'ready' | 'auth-required' | 'unavailable' };
    fallback: { provider: string; status: 'ready' | 'auth-required' | 'unavailable' };
  };
  timestamp: string;
}

export interface DataProvenance {
  calculatedAt: string;
  modelVersion: string;
  elevationProvider: string;
  obstacleProvider: string;
  resolutionM: number;
}

export type EquipmentPolarization = 'horizontal' | 'vertical' | 'dual' | 'circular';
export type EquipmentLifecycle = 'current' | 'vintage' | 'legacy' | 'discontinued' | 'unknown';
export type EquipmentSpecStatus = 'official' | 'official-partial' | 'engineering-profile' | 'unverified';

export interface RadiationPatternPoint {
  angleDeg: number;
  attenuationDb: number;
}

export interface EquipmentPerformanceEntry {
  name: string;
  modulation: string;
  sensitivityDbm: number;
  minimumSnrDb: number;
  phyRateMbps: number;
  usableThroughputMbps: number;
}

export interface EquipmentPerformanceProfile {
  source: string;
  channelWidthMHz: number;
  entries: EquipmentPerformanceEntry[];
}

export interface EquipmentManufacturer {
  id: string;
  name: string;
  slug: string;
  isSystem: boolean;
  isActive: boolean;
}

export interface RadioDeviceRecord {
  id: string;
  manufacturerId: string;
  manufacturerName: string;
  model: string;
  sku: string | null;
  deviceType: string;
  roles: string[];
  lifecycle: EquipmentLifecycle;
  compatibilityTags: string[];
  supportedBands: string[];
  minFrequencyMHz: number | null;
  maxFrequencyMHz: number | null;
  maxTxPowerDbm: number | null;
  channelWidthsMHz: number[];
  receiverSensitivityDbm: number | null;
  maxThroughputMbps: number | null;
  maxRangeKm?: number | null;
  antennaIntegrated: boolean;
  antennaGainDbi: number | null;
  polarization: EquipmentPolarization;
  performanceProfiles: EquipmentPerformanceProfile[];
  notes: string;
  sourceUrl: string | null;
  sourceTitle: string | null;
  sourceRetrievedOn: string | null;
  catalogVersion: string | null;
  specStatus: EquipmentSpecStatus;
  isBuiltIn: boolean;
  isCustom: boolean;
  isActive: boolean;
}

export interface AntennaModelRecord {
  id: string;
  manufacturerId: string;
  manufacturerName: string;
  model: string;
  sku: string | null;
  roles: string[];
  lifecycle: EquipmentLifecycle;
  compatibilityTags: string[];
  supportedBands: string[];
  minFrequencyMHz: number | null;
  maxFrequencyMHz: number | null;
  peakGainDbi: number | null;
  horizontalBeamwidthDeg: number | null;
  verticalBeamwidthDeg: number | null;
  polarization: EquipmentPolarization;
  frontToBackDb: number | null;
  patternHorizontal: RadiationPatternPoint[];
  patternVertical: RadiationPatternPoint[];
  patternApproximate: boolean;
  notes: string;
  sourceUrl: string | null;
  sourceTitle: string | null;
  sourceRetrievedOn: string | null;
  catalogVersion: string | null;
  specStatus: EquipmentSpecStatus;
  isBuiltIn: boolean;
  isCustom: boolean;
  isActive: boolean;
}

export interface EquipmentCatalogFilter {
  query?: string;
  band?: string;
  role?: string;
  lifecycle?: EquipmentLifecycle;
}

export function filterEquipmentCatalog<T extends RadioDeviceRecord | AntennaModelRecord>(items: T[], filter: EquipmentCatalogFilter): T[] {
  const needle = filter.query?.trim().toLocaleLowerCase('en-US') ?? '';
  return items.filter((item) => (!needle || `${item.model} ${item.sku ?? ''}`.toLocaleLowerCase('en-US').includes(needle))
    && (!filter.band || item.supportedBands.includes(filter.band))
    && (!filter.role || item.roles.includes(filter.role))
    && (!filter.lifecycle || item.lifecycle === filter.lifecycle));
}

export function builtInEquipmentIsProtected(item: Pick<RadioDeviceRecord | AntennaModelRecord, 'isBuiltIn' | 'isCustom'>): boolean {
  return item.isBuiltIn || !item.isCustom;
}

export interface CoverageBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface CoverageMatrixPayload {
  bounds: CoverageBounds;
  rows: number;
  cols: number;
  resolutionM: number;
  valuesBase64: string;
  noCoverageDbm: number;
  minimumDbm: number;
  maximumDbm: number;
  coveredCellCount: number;
}

export type CoverageJobStatus = 'queued' | 'sampling' | 'running' | 'completed' | 'failed' | 'cancelled';

export interface CoverageJobSnapshot {
  id: string;
  status: CoverageJobStatus;
  progress: number;
  error: string | null;
  result: CoverageMatrixPayload | null;
  provenance: DataProvenance | null;
}

export type ClientAvailability = 'available' | 'height_required' | 'unavailable';

export interface ClientAccessPointCandidate {
  accessPointId: string;
  accessPointName: string;
  hasLineOfSight: boolean;
  fresnelClearancePercent: number;
  snrDb: number;
  fadeMarginDb: number;
  throughputMbps: number;
  loadPercent: number;
  predictedInterferenceDbm: number;
  distanceM: number;
  recommendedClientHeightM: number | null;
  heightTargetReachable: boolean;
}

export interface ClientAnalysisItem {
  clientId: string;
  clientName: string;
  availability: ClientAvailability;
  bestAccessPointId: string | null;
  bestAccessPointName: string | null;
  currentHeightM: number;
  recommendedHeightM: number | null;
  reasons: string[];
  candidates: ClientAccessPointCandidate[];
}

export interface ClientAnalysisSummary {
  total: number;
  available: number;
  heightRequired: number;
  unavailable: number;
}

export interface ClientBatchAnalysis {
  summary: ClientAnalysisSummary;
  clients: ClientAnalysisItem[];
  calculatedAt: string;
  modelVersion: string;
}

export interface BackupManifest {
  formatVersion: 1;
  appVersion: string;
  schemaVersion: string;
  createdAt: string;
  kind: 'manual' | 'safety' | 'uploaded';
  databaseFile: 'database.dump';
  dumpSizeBytes: number;
  dumpSha256: string;
  includesDemCache: false;
}

export interface BackupSummary {
  id: string;
  filename: string;
  sizeBytes: number;
  createdAt: string;
  kind: BackupManifest['kind'];
  appVersion: string;
  schemaVersion: string;
}

export interface BackupValidation {
  backup: BackupSummary;
  manifest: BackupManifest;
  compatible: boolean;
  currentSchemaVersion: string;
  freeSpaceBytes: number;
  requiredSpaceBytes: number;
  confirmationToken: string;
  confirmationExpiresAt: string;
}

export interface AdminSystemStatus {
  appVersion: string;
  database: 'connected' | 'unavailable';
  postgisVersion: string | null;
  schemaVersion: string | null;
  maintenanceMode: boolean;
  backupPath: string;
  pgDumpAvailable: boolean;
  pgRestoreAvailable: boolean;
  demCacheSizeBytes: number;
  diskFreeBytes: number | null;
  activeCoverageJobs: number;
  queuedCoverageJobs: number;
  failedCoverageJobs: number;
  mapProvider: string;
  elevationProvider: string;
  registrationAllowed: boolean;
  trustProxyHops: number;
  demCacheLimitBytes: number;
}

export interface AdminUserRecord {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  isActive: boolean;
  emailVerified: boolean;
  emailVerifiedAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
}

export interface AdminSmtpSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  fromAddress: string;
  fromName: string;
  passwordConfigured: boolean;
  provider: 'yandex' | 'custom';
  updatedAt: string | null;
}

export interface AdminSmtpSettingsUpdate {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  fromAddress: string;
  fromName: string;
  password?: string;
}

export type OAuthProviderName = 'google' | 'yandex';

export interface AdminOAuthProviderSettings {
  enabled: boolean;
  clientId: string;
  clientSecretConfigured: boolean;
  callbackUrl: string;
}

export interface AdminOAuthSettings {
  google: AdminOAuthProviderSettings;
  yandex: AdminOAuthProviderSettings;
  updatedAt: string | null;
}

export interface AdminOAuthProviderSettingsUpdate {
  enabled: boolean;
  clientId: string;
  clientSecret?: string;
}

export interface AdminOAuthSettingsUpdate {
  google: AdminOAuthProviderSettingsUpdate;
  yandex: AdminOAuthProviderSettingsUpdate;
}

export interface AuditEventRecord {
  id: string;
  actorUserId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  details: Record<string, unknown>;
  occurredAt: string;
}
